/**
 * Monitors: watch a template, a preset or a list of URLs on a schedule and get told what changed.
 *
 *   const { monitors } = await fetchMonitors();
 *   const m = await createMonitor(draft);            first check runs right away and records the starting point
 *   const est = await estimateMonitor(draft);        { creditsPerCheck, checksPerMonth, creditsPerMonth }
 *   const { checks } = await fetchChecks(m.id);      history; fetchCheck(m.id, checkId) has the changes
 */
import api from "@/lib/axios";
import type { TemplateSite } from "./templates.service";

export type SourceKind = "template" | "preset" | "urls";

export interface MonitorSource {
  id?: string;
  kind: SourceKind;
  label?: string;
  template?: string;
  inputs?: Record<string, unknown>;
  presetId?: string;
  urls?: string[];
  site?: TemplateSite | null;
}

export type AlertOn = "new" | "changed" | "removed" | "page" | "every";
export type Every = "5m" | "15m" | "30m" | "1h" | "6h" | "12h" | "1d" | "1w";
export type RuleOp = "lt" | "lte" | "gt" | "gte" | "eq" | "neq" | "contains" | "drops_by_pct" | "rises_by_pct";

export interface MonitorRule {
  field: string;
  op: RuleOp;
  value: number | string;
}

export interface MonitorSchedule {
  every?: Every;
  at?: string;
  day?: number;
  cron?: string;
  timezone?: string;
}

export interface MonitorNotify {
  email?: { enabled: boolean; recipients?: string[] };
  webhook?: { url: string; secret?: string };
  slack?: { webhookUrl: string };
  discord?: { webhookUrl: string };
}

export interface MonitorDraft {
  name?: string;
  sources: MonitorSource[];
  schedule: MonitorSchedule;
  alertOn: AlertOn;
  watchFields?: string[];
  rule?: MonitorRule | null;
  goal?: string | null;
  notify: MonitorNotify;
}

export interface Monitor extends MonitorDraft {
  id: string;
  name: string;
  status: "active" | "paused";
  cron: string;
  nextRunAt: string | null;
  lastCheckAt: string | null;
  lastChangeAt: string | null;
  lastStatus: string | null;
  lastSummary: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FieldChange {
  field: string;
  previous: unknown;
  current: unknown;
}

export interface SourceChanges {
  sourceId: string;
  label: string;
  baseline: boolean;
  added: Record<string, unknown>[];
  removed: Record<string, unknown>[];
  changed: { key: string; item: Record<string, unknown>; fields: FieldChange[] }[];
  pages: { url: string; added: string[]; removed: string[] }[];
  rows?: Record<string, unknown>[];
  rowCount: number;
  error?: string;
}

export interface MonitorCheck {
  id: string;
  trigger: "schedule" | "manual";
  status: "running" | "completed" | "partial" | "failed" | "skipped";
  startedAt: string;
  finishedAt: string | null;
  creditsUsed: number | null;
  hasChanges: boolean;
  summary: string | null;
  notified: boolean;
  judgment: { meaningful: boolean; reason: string } | null;
  error: string | null;
  sources?: SourceChanges[];
}

export interface FieldError {
  field: string;
  message: string;
}

export class MonitorInputError extends Error {
  errors: FieldError[];
  constructor(message: string, errors: FieldError[]) {
    super(message);
    this.errors = errors;
  }
}

function rethrow(err: unknown): never {
  const e = err as { message?: string; response?: { status?: number; data?: { message?: string; errors?: FieldError[] } } };
  const data = e?.response?.data;
  if (e?.response?.status === 422 && Array.isArray(data?.errors)) throw new MonitorInputError(data?.message || "Some fields need fixing.", data!.errors!);
  throw new Error(data?.message || e?.message || "Something went wrong.");
}

const wrap = async <T>(fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn();
  } catch (err) {
    rethrow(err);
  }
};

export const fetchMonitors = () => wrap(async () => (await api.get("/monitors")).data.monitors as Monitor[]);
export const fetchMonitor = (id: string) => wrap(async () => (await api.get(`/monitors/${encodeURIComponent(id)}`)).data.monitor as Monitor);
export interface SavedMonitor {
  monitor: Monitor;
  /** Extra email addresses and whether they confirmed ("pending" ones get a confirmation email). */
  recipients: { email: string; status: "pending" | "confirmed" | "unsubscribed" }[];
}
export const createMonitor = (d: MonitorDraft) =>
  wrap(async () => {
    const r = (await api.post("/monitors", d)).data;
    return { monitor: r.monitor, recipients: r.recipients ?? [] } as SavedMonitor;
  });
export const updateMonitor = (id: string, d: Partial<MonitorDraft>) =>
  wrap(async () => {
    const r = (await api.patch(`/monitors/${encodeURIComponent(id)}`, d)).data;
    return { monitor: r.monitor, recipients: r.recipients ?? [] } as SavedMonitor;
  });
export const fetchMonitorLimits = () =>
  wrap(async () => {
    const r = (await api.get("/monitors/limits")).data;
    return { plan: r.plan as string, minIntervalMinutes: r.minIntervalMinutes as number, maxMonitors: r.maxMonitors as number, used: r.used as number };
  });
export const deleteMonitor = (id: string) => wrap(async () => void (await api.delete(`/monitors/${encodeURIComponent(id)}`)));
export const checkMonitorNow = (id: string) => wrap(async () => void (await api.post(`/monitors/${encodeURIComponent(id)}/check`)));
export const setMonitorPaused = (id: string, paused: boolean) =>
  wrap(async () => (await api.post(`/monitors/${encodeURIComponent(id)}/${paused ? "pause" : "resume"}`)).data.monitor as Monitor);
export const fetchChecks = (id: string, page = 1, limit = 20) =>
  wrap(async () => {
    const r = await api.get(`/monitors/${encodeURIComponent(id)}/checks`, { params: { page, limit } });
    return { checks: r.data.checks as MonitorCheck[], total: r.data.total as number };
  });
export const fetchCheck = (id: string, checkId: string) =>
  wrap(async () => (await api.get(`/monitors/${encodeURIComponent(id)}/checks/${encodeURIComponent(checkId)}`)).data.check as MonitorCheck);
export const estimateMonitor = (d: MonitorDraft) =>
  wrap(async () => {
    const r = await api.post("/monitors/estimate", d);
    return r.data as { creditsPerCheck: number; checksPerMonth: number; creditsPerMonth: number; usesAi: boolean };
  });
export const testNotification = (notify: MonitorNotify, name?: string) =>
  wrap(async () => (await api.post("/monitors/test-notification", { notify, name })).data as { sent: number; errors: string[] });
